import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OrgRole } from "@talenthop/shared";
import { useAuth } from "../../lib/AuthContext.js";
import { api, apiFetch, ApiError } from "../../lib/api.js";
import { useOrgPlans } from "./planUsage.js";
import { Users, Info, CheckBig } from "../../components/icons.js";
import { Modal } from "../admin/adminUi.js";

interface Member {
  userId: string;
  name: string;
  email: string;
  role: OrgRole;
  status: "active" | "invited" | "suspended";
}
interface MembersResponse {
  members: Member[];
}
interface InviteMemberResponse {
  member: Member & { orgId: string };
  invited: boolean;
  invitedBy: string;
}

/**
 * Four primary roles are offered in the pickers and the legend. The enum still carries
 * the legacy roles (hiring_manager, interviewer, external_client) for compatibility — their
 * duties are folded into the four below — so ROLE_LABEL keeps a label for every enum value
 * to render any member already sitting on a legacy role.
 */
const PRIMARY_ROLES: OrgRole[] = ["super_admin", "recruiter", "reviewer", "billing_admin"];

const ROLE_LABEL: Record<OrgRole, string> = {
  super_admin: "Super Admin",
  recruiter: "Recruiter",
  reviewer: "Reviewer",
  billing_admin: "Billing",
  hiring_manager: "Hiring Manager",
  interviewer: "Interviewer",
  external_client: "External Client · scoped",
};

const ROLE_DESC: Record<OrgRole, string> = {
  super_admin: "Full control — org settings, all requests, billing & people",
  recruiter: "Create & run requests, invite, review, and decide (human-gated)",
  reviewer: "Read reports & notes; no actions",
  billing_admin: "Plans & billing; candidate PII redacted",
  hiring_manager: "Advance / reject (human-gated), own a team",
  interviewer: "Run & observe interviews, add notes",
  external_client: "Delegated, scoped to shared candidates, revocable anytime",
};

/** Options for a member's role select: the four primary roles, plus their current role
 *  if it's a legacy one — so an existing assignment stays visible and can be migrated. */
const roleOptions = (current: OrgRole): OrgRole[] =>
  PRIMARY_ROLES.includes(current) ? PRIMARY_ROLES : [...PRIMARY_ROLES, current];

/** Role is identity, not status: brand pill for the super admin, neutral for everyone else (blue
 *  means "in progress" in Theme 1, so Billing no longer borrows it). */
const rolePill = (role: OrgRole): string => (role === "super_admin" ? "pill t" : "pill n");

/** Green = active only; invited (pending) and suspended stay neutral (brief rule 4). */
const statusPill = (status: Member["status"]): string => (status === "active" ? "pill v" : "pill n");

const initialsOf = (name: string, email: string): string => {
  const parts = (name || email).trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
};

/** Tracks a media query so the two-column layout can stack instead of clipping the invite rail. */
function useNarrow(query: string): boolean {
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (): void => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return narrow;
}

/** Users · team & roles (GET/POST/PATCH /orgs/{orgId}/members): live member table with
 *  inline role changes and an invite form — invitees receive a secure set-password link by
 *  email; no password is ever created or shown here. */
export function Team(): ReactNode {
  const { me } = useAuth();
  const orgId = me?.orgId ?? "";
  const isSuperAdmin = me?.role === "super_admin";
  const queryClient = useQueryClient();
  // 0031: per-member monthly credit budgets (shared pool by default). canBuy comes from the
  // same response and is the SERVER's buyer verdict (super_admin | billing_admin | owner) —
  // gate the budget controls on it, not on isSuperAdmin, which would wrongly exclude billing.
  const budgetsQuery = useQuery({
    queryKey: ["org-credits"],
    queryFn: () =>
      api.get<{ canBuy: boolean; budgets: { userId: string; name: string; monthly: number }[] }>("/org/credits"),
    staleTime: 15_000,
  });
  const budgets = budgetsQuery.data?.budgets ?? [];
  const canBuy = budgetsQuery.data?.canBuy ?? false;
  // 0094: member budgets cap the PRE-PLANS prepaid balance only (plan allowances are a shared
  // pool). The column is shown only to orgs that still have that balance or a budget set, so plan
  // customers never see a credits-denominated control.
  const plansQuery = useOrgPlans();
  const showBudgets = budgets.length > 0 || (plansQuery.data?.legacyBalance ?? 0) > 0;
  const setBudget = useMutation({
    mutationFn: (v: { userId: string; monthlyCredits: number | null }) => api.put("/org/credits/budgets", v),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["org-credits"] }),
  });

  // Budget editor modal — replaces the old hardcoded "Set budget = 100" and the +20/−20 row.
  const [budgetTarget, setBudgetTarget] = useState<{ userId: string; name: string; monthly: number | null } | null>(null);
  const [budgetAmount, setBudgetAmount] = useState("");
  const openBudgetEditor = (m: Member, current: number | null): void => {
    setBudget.reset(); // a stale error from an earlier attempt shouldn't haunt a fresh editor
    setBudgetTarget({ userId: m.userId, name: m.name, monthly: current });
    setBudgetAmount(String(current ?? 100));
  };
  const budgetAmountNum = Number(budgetAmount);
  const budgetAmountValid = Number.isInteger(budgetAmountNum) && budgetAmountNum >= 1 && budgetAmountNum <= 100000;

  const narrow = useNarrow("(max-width: 1200px)");


  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState<OrgRole>("recruiter");
  const [invitedEmail, setInvitedEmail] = useState("");
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["members", orgId],
    queryFn: () => api.get<MembersResponse>(`/orgs/${orgId}/members`),
    enabled: orgId.length > 0,
  });
  const members = data?.members ?? [];

  const invite = useMutation({
    mutationFn: () =>
      api.post<InviteMemberResponse>(`/orgs/${orgId}/members`, {
        email: invEmail.trim(),
        role: invRole,
      }),
    onSuccess: () => {
      setInvitedEmail(invEmail.trim());
      setInvEmail("");
      void queryClient.invalidateQueries({ queryKey: ["members", orgId] });
    },
  });

  const patchMember = useMutation({
    mutationFn: (input: { userId: string; role?: OrgRole; status?: Member["status"] }) =>
      apiFetch(`/orgs/${orgId}/members/${input.userId}`, {
        method: "PATCH",
        body: { role: input.role, status: input.status },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["members", orgId] });
    },
  });

  const inviteError =
    invite.error instanceof ApiError && invite.error.status === 409
      ? "That person is already a member of this org."
      : invite.error instanceof ApiError && invite.error.status === 400
        ? "Check the email address — the API rejected it."
        : invite.isError
          ? "Couldn't send the invite. Please retry in a moment."
          : null;

  return (
    <section className="t1s-page t1s-team">
      <div className="page-h">
        <div>
          <h1>Users & roles</h1>
          <div className="sub">Your org's people and their access · live from the API</div>
        </div>
        <span className="pill t">
          {members.length} member{members.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="banner t1s-neutral">
        <Info />
        <div>
          This is <b>your org's</b> admin — people and their access. Board, liveness and payment
          integrations are managed by TalentHop at the platform level, not here.
        </div>
      </div>

      {invitedEmail && (
        <div className="banner ok">
          <CheckBig />
          <div>
            <b>Invite sent to {invitedEmail}.</b> They'll get an email with a secure link to set
            their own password and join your org — no password is created or shared here.
            <button className="btn sm ghost" style={{ marginLeft: 10 }} onClick={() => setInvitedEmail("")}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* minmax(0, …): grid items default to min-width:auto, so the wide member table used to
          blow the 1.5fr track past the pane and clip the invite rail. Capping the minimum lets
          .tbl-wrap scroll the table internally; under ~1200px the rail stacks below instead. */}
      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          {isLoading && <div className="card pad mut sm">Loading your team…</div>}

          {isError && (
            <div className="banner crit">
              <span>Couldn't load your team. Please retry in a moment.</span>
            </div>
          )}

          {!isLoading && !isError && members.length === 0 && (
            <div className="card pad">
              <p className="mut" style={{ margin: 0 }}>
                No members yet — invite your first teammate below.
              </p>
            </div>
          )}

          {!isLoading && !isError && members.length > 0 && (
            <div className="card">
              <div className="card-h">
                <h3>Team</h3>
                <span className="mut sm">role changes apply immediately</span>
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      {showBudgets && <th>Prepaid budget</th>}
                      <th className="t1s-actions-h">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.userId}>
                        <td>
                          <div className="t1s-user">
                            <span className="t1s-avatar" aria-hidden="true">{initialsOf(m.name, m.email)}</span>
                            <div style={{ minWidth: 0 }}>
                              <div className="t1s-ellipsis" style={{ fontWeight: 600 }} title={m.name}>
                                {m.name}
                                {m.userId === me?.userId && <span className="mut sm" style={{ fontWeight: 500 }}> · you</span>}
                              </div>
                              <div className="mut sm t1s-ellipsis" title={m.email}>{m.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {/* One place for the role (audit 23: it was shown twice). Editable rows get the
                              select; everyone else — and your own row — sees the pill. */}
                          {isSuperAdmin && m.userId !== me?.userId ? (
                            <select
                              className="t1s-role-select"
                              aria-label={`Role for ${m.name}`}
                              value={m.role}
                              disabled={patchMember.isPending}
                              onChange={(e) =>
                                patchMember.mutate({ userId: m.userId, role: e.target.value as OrgRole })
                              }
                            >
                              {roleOptions(m.role).map((r) => (
                                <option key={r} value={r}>
                                  {PRIMARY_ROLES.includes(r) ? ROLE_LABEL[r] : `${ROLE_LABEL[r]} (older role)`}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span
                              className={rolePill(m.role)}
                              data-tip={
                                !isSuperAdmin
                                  ? "Only a super admin can change roles"
                                  : "You can't change your own role"
                              }
                            >
                              {ROLE_LABEL[m.role] ?? m.role}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={statusPill(m.status)} style={{ textTransform: "capitalize" }}>
                            {m.status}
                          </span>
                        </td>
                        {showBudgets && <td>
                          {(() => {
                            const b = budgets.find((x) => x.userId === m.userId);
                            return (
                              <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                                {b ? (
                                  <span className="tnum" style={{ fontWeight: 650 }}>{b.monthly}/mo</span>
                                ) : (
                                  <span className="pill n">shared pool</span>
                                )}
                                {/* PUT /org/credits/budgets is buyer-gated server-side — showing
                                    the control to anyone else just manufactures a 403. */}
                                {canBuy && (
                                  <button
                                    className="btn sm ghost"
                                    disabled={setBudget.isPending}
                                    onClick={() => openBudgetEditor(m, b?.monthly ?? null)}
                                  >
                                    Edit budget
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                        </td>}
                        <td className="t1s-actions-c">
                          <div className="t1s-actions">
                            {m.status === "suspended" ? (
                              <button
                                className="btn sm"
                                disabled={!isSuperAdmin || patchMember.isPending}
                                data-tip={!isSuperAdmin ? "Only a super admin can reactivate" : undefined}
                                data-tip-pos="left"
                                onClick={() => patchMember.mutate({ userId: m.userId, status: "active" })}
                              >
                                Reactivate
                              </button>
                            ) : (
                              <button
                                className="btn sm danger"
                                // Only an org super_admin can suspend, and never yourself — avoids an
                                // accidental self-lockout. Suspension revokes access but keeps the
                                // membership row and audit trail intact (no hard delete).
                                disabled={!isSuperAdmin || m.userId === me?.userId}
                                data-tip={
                                  !isSuperAdmin
                                    ? "Only a super admin can remove people"
                                    : m.userId === me?.userId
                                      ? "You can't remove yourself"
                                      : undefined
                                }
                                data-tip-pos="left"
                                onClick={() => setRemoveTarget(m)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {patchMember.isError && (
            <div className="banner crit" style={{ marginTop: 12 }}>
              <span>
                {patchMember.error instanceof ApiError && patchMember.error.status === 403
                  ? "You don't have permission for that — this action is super admin-only."
                  : "Couldn't update that member. Please retry."}
              </span>
            </div>
          )}

          {setBudget.isError && (
            <div className="banner crit" style={{ marginTop: 12 }}>
              <span>
                {setBudget.error instanceof ApiError && setBudget.error.status === 403
                  ? "You don't have permission to change budgets — that needs a super admin or billing admin."
                  : "Couldn't update that budget. Please retry."}
              </span>
            </div>
          )}
        </div>

        <div className={`t1s-two${narrow ? " narrow" : ""}`}>
          <div className="card">
            <div className="card-h">
              <h3>Invite user</h3>
              <span className="mut sm">They get a secure link by email</span>
            </div>
            <div className="card-b">
              <div className="field">
                <label htmlFor="inv-email">Email</label>
                <input
                  id="inv-email"
                  className="input"
                  type="email"
                  value={invEmail}
                  onChange={(e) => setInvEmail(e.target.value)}
                  placeholder="name@yourcompany.com"
                />
              </div>
              <div className="field">
                <label htmlFor="inv-role">Role</label>
                <select id="inv-role" value={invRole} onChange={(e) => setInvRole(e.target.value as OrgRole)}>
                  {PRIMARY_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_LABEL[r]}
                    </option>
                  ))}
                </select>
              </div>
              {inviteError && (
                <div className="banner crit" style={{ marginBottom: 12 }}>
                  <span>{inviteError}</span>
                </div>
              )}
              <div className="t1s-invite-row">
                <button
                  className="btn primary"
                  disabled={!/^\S+@\S+\.\S+$/.test(invEmail.trim()) || invite.isPending}
                  onClick={() => invite.mutate()}
                >
                  <Users /> {invite.isPending ? "Inviting…" : "Invite user"}
                </button>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-h">
              <h3>Roles & what they can do</h3>
            </div>
            <div className="card-b t1s-roles">
              {PRIMARY_ROLES.map((r) => (
                <div key={r}>
                  <span>
                    <span className={rolePill(r)}>{ROLE_LABEL[r]}</span>
                  </span>
                  <span>{ROLE_DESC[r]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="disclaimer">
        Roles are scoped and revocable — Reviewer is read-only and Billing sees ROI with candidate PII
        redacted. Rejection is human-gated by design and cannot be turned off.
      </div>

      {budgetTarget && (
        <Modal
          title={`Monthly budget for ${budgetTarget.name}`}
          onClose={() => setBudgetTarget(null)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              {budgetTarget.monthly != null && (
                <button
                  className="btn ghost"
                  disabled={setBudget.isPending}
                  onClick={() =>
                    setBudget.mutate(
                      { userId: budgetTarget.userId, monthlyCredits: null },
                      { onSuccess: () => setBudgetTarget(null) },
                    )
                  }
                >
                  Remove cap
                </button>
              )}
              <button className="btn ghost" onClick={() => setBudgetTarget(null)}>
                Cancel
              </button>
              <button
                className="btn primary"
                disabled={!budgetAmountValid || setBudget.isPending}
                onClick={() =>
                  setBudget.mutate(
                    { userId: budgetTarget.userId, monthlyCredits: budgetAmountNum },
                    { onSuccess: () => setBudgetTarget(null) },
                  )
                }
              >
                {setBudget.isPending ? "Saving…" : "Save budget"}
              </button>
            </div>
          }
        >
          <p className="mut sm" style={{ marginTop: 0 }}>
            Caps how much of the organisation's pre-plans prepaid balance {budgetTarget.name} can use
            each month (a resume uses 1 unit, an AI interview 10). Plan allowances are always shared.
          </p>
          <div className="field">
            <label htmlFor="budget-amount">Units per month</label>
            <input
              id="budget-amount"
              className="input tnum"
              type="number"
              min={1}
              max={100000}
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(e.target.value)}
            />
          </div>
          {!budgetAmountValid && budgetAmount !== "" && (
            <div className="mut sm" style={{ color: "var(--crit)" }}>
              Enter a whole number between 1 and 100,000.
            </div>
          )}
          {setBudget.isError && (
            <div className="banner crit" style={{ marginTop: 10 }}>
              <span>
                {setBudget.error instanceof ApiError && setBudget.error.status === 403
                  ? "You don't have permission to change budgets — that needs a super admin or billing admin."
                  : "Couldn't update that budget. Please retry."}
              </span>
            </div>
          )}
        </Modal>
      )}

      {removeTarget && (
        <Modal title={`Remove ${removeTarget.name} from the team?`} onClose={() => setRemoveTarget(null)}>
          <p className="mut sm" style={{ marginTop: 0 }}>
            Their access is revoked immediately — they can no longer sign in to this org. Their
            membership record and history stay intact and a super admin can reactivate them anytime.
          </p>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn ghost" onClick={() => setRemoveTarget(null)}>
              Cancel
            </button>
            <button
              className="btn danger"
              disabled={patchMember.isPending}
              onClick={() =>
                patchMember.mutate(
                  { userId: removeTarget.userId, status: "suspended" },
                  { onSuccess: () => setRemoveTarget(null) },
                )
              }
            >
              {patchMember.isPending ? "Removing…" : "Remove access"}
            </button>
          </div>
        </Modal>
      )}
    </section>
  );
}
